import {
  TableProps,
  TableConfigState,
  TableConfigChangeType,
  RowConfig,
  ColumnConfig,
} from '@/shared/types/table';
import { cn } from '@/shared/utils/cn';
import { TableToolbar } from './components/organisms/TableToolbar';
import { TableContent } from './components/organisms/TableContent';
import { TableSelectionBar } from './components/organisms/TableSelectionBar';
import { Pagination } from '@/modules/Pagination';
import { useTableSort } from './hooks/useTableSort';
import { useTableSelection } from './hooks/useTableSelection';
import { useTableColumns } from './hooks/useTableColumns';
import { useTableExpand } from './hooks/useTableExpand';
import { useTableFocus } from './hooks/useTableFocus';
import { useTableFilters } from './hooks/useTableFilters';
import { useTableEdit } from './hooks/useTableEdit';
import { useTableConfig } from './hooks/useTableConfig';
import { useRef, useEffect, useState, useMemo } from 'react';
import { getNextSortConfig } from './utils/sorting';
import {
  applyAutoResizeFlagsToColumns,
  buildColumnsWithEditActions,
} from './utils/tableColumnTransforms';
import {
  filterRowsWithAdvancedFilters,
  paginateRowsForDisplay,
  resolveEffectiveEmptyState,
  searchRowsWithColumns,
  sortRowsByColumn,
} from './utils/tableDataPipeline';
import { isRowExcludedFromSelection } from './utils/excludedRows';
import { isActionsColumn, isColumnLockable } from './utils/columnLockable';
import { AdvancedFilterConfig } from './types/filter';
export const Table = <T,>({
  columns: initialColumns,
  rows: initialRows,
  loading = false,
  empty = {
    title: 'Aun no hay nada aquí...',
    description: 'Aun no hay contenido disponible por aquí para mostrar.',
  },
  selection,
  excludedRows,
  pagination: paginationConfig,
  toolbar: toolbarConfig,
  sort: initialSort,
  filters: initialFilters,
  expandable = false,
  resizable = false,
  columnsLockable,
  striped = false,
  bordered = false,
  borderable = false,
  hoverable = true,
  dense = false,
  stickyHeader = true,
  stickyCheckbox = true,
  stickyActions = true,
  showEditActions,
  configKey,
  persistConfig = false,
  queryPersist = true,
  onConfigChange,
  className,
  onSort,
  onFilter,
  onColumnResize,
  onColumnReorder,
  onColumnVisibilityChange,
  onRowClick,
  onRowExpand,
  onHandleChange,
  editingRowIds,
  onEditingRowIdsChange,
  onEditModeChange,
  onSuccessComplete,
}: TableProps<T>) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const [resizeHoverColumnId, setResizeHoverColumnId] = useState<string | null>(null);
  // Rastrear qué filas fueron editadas (tienen cambios en sus celdas)
  const editedRowIdsRef = useRef<Set<string | number>>(new Set());
  const prevEditingRowIdsRef = useRef<Set<string | number>>(new Set());

  // Hook para persistencia de configuración
  const tableConfig = useTableConfig({
    configKey,
    persistConfig,
    queryPersist,
    initialColumns: initialColumns as ColumnConfig<unknown>[],
    initialSort,
    initialFilters,
    onConfigChange: onConfigChange as
      | ((config: TableConfigState, changeType: string) => void)
      | undefined,
  });

  // Filtros iniciales: desde config persistido si queryPersist, si no desde props
  const effectiveInitialFilters =
    persistConfig && configKey && queryPersist
      ? (tableConfig.filters ?? initialFilters ?? [])
      : (initialFilters ?? []);

  // Usar columnas del config si está habilitada la persistencia
  const effectiveColumns = (
    persistConfig && configKey ? tableConfig.columns : initialColumns
  ) as ColumnConfig<T>[];

  // Detectar si hay columnas editables
  const hasEditableColumns = useMemo(() => {
    return effectiveColumns.some((col) => col.editable && col.editable !== 'none');
  }, [effectiveColumns]);

  const columnsWithEditActions = useMemo(
    () =>
      buildColumnsWithEditActions<T>({
        effectiveColumns,
        hasEditableColumns,
        showEditActions,
      }),
    [effectiveColumns, hasEditableColumns, showEditActions]
  );

  const columnsWithAutoResize = useMemo(
    () => applyAutoResizeFlagsToColumns(columnsWithEditActions),
    [columnsWithEditActions]
  );

  const { columns, handleSetVisibility, handleToggleLock, handleResize, setColumnsOrder } =
    useTableColumns(columnsWithAutoResize);

  const { sortConfig, handleSort } = useTableSort(initialSort);

  const isRowSelectableForSelection = useMemo(
    () => (row: RowConfig<T>) =>
      row.disabled !== true && !isRowExcludedFromSelection(row, excludedRows),
    [excludedRows]
  );

  const { handleRowExpand: handleExpand, applyExpandedState } = useTableExpand<T>();

  const { handleCellFocus, handleCellBlur, isCellFocused } = useTableFocus();

  const { filters, setAllFilters } = useTableFilters({
    initialFilters: effectiveInitialFilters,
    controlledFilters: persistConfig && configKey && queryPersist ? undefined : initialFilters,
    onFiltersChange: (newFilters) => {
      onFilter?.(newFilters);
      if (persistConfig && configKey && queryPersist) {
        tableConfig.updateFilters(newFilters);
      }
    },
  });

  const { toggleRowEdit, applyEditState } = useTableEdit<T>({
    externalEditingRowIds: editingRowIds,
    onEditingRowIdsChange,
    onEditModeChange,
  });

  // Estado para búsqueda interna (inicial desde config si queryPersist)
  const [internalSearchQuery, setInternalSearchQuery] = useState(
    () => (persistConfig && configKey && queryPersist ? tableConfig.searchQuery : '') ?? ''
  );

  // Detectar clicks fuera de la tabla para quitar el focus
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tableRef.current && !tableRef.current.contains(event.target as Node)) {
        handleCellBlur();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleCellBlur]);

  // Referencia para detectar cambios en filtros y búsqueda
  const prevFiltersLengthRef = useRef(filters.length);
  const prevSearchQueryRef = useRef(internalSearchQuery);

  // Resetear a la página 1 cuando cambian los filtros o la búsqueda
  useEffect(() => {
    const filtersChanged = prevFiltersLengthRef.current !== filters.length;
    const searchChanged = prevSearchQueryRef.current !== internalSearchQuery;

    if (paginationConfig?.enabled && (filtersChanged || searchChanged)) {
      // Solo resetear si estamos en una página diferente a la 1
      if (paginationConfig.currentPage !== 1) {
        paginationConfig?.onPageChange?.(1);
      }
    }

    prevFiltersLengthRef.current = filters.length;
    prevSearchQueryRef.current = internalSearchQuery;
  }, [filters.length, internalSearchQuery, paginationConfig]);

  const searchedRows = useMemo(
    () =>
      searchRowsWithColumns({
        initialRows,
        columns,
        internalSearchQuery,
        internalSearchEnabled: toolbarConfig?.internalSearch,
      }),
    [initialRows, internalSearchQuery, columns, toolbarConfig?.internalSearch]
  );

  const filteredRows = useMemo(
    () =>
      filterRowsWithAdvancedFilters({
        searchedRows,
        filters,
        columns,
        internalFiltersEnabled: toolbarConfig?.internalFilters,
      }),
    [searchedRows, filters, columns, toolbarConfig?.internalFilters]
  );

  const sortedRows = useMemo(
    () =>
      sortRowsByColumn({
        filteredRows,
        sortConfig,
        columns,
      }),
    [filteredRows, sortConfig, columns]
  );

  // Calcular el total después de aplicar filtros (para paginación correcta)
  const totalAfterFilters = sortedRows.length;

  const paginatedRows = useMemo(
    () =>
      paginateRowsForDisplay({
        sortedRows,
        paginationConfig,
        toolbarConfig,
        totalAfterFilters,
      }),
    [sortedRows, paginationConfig, totalAfterFilters, toolbarConfig]
  );

  // Aplicar expansión a las filas paginadas
  const expandedRows = applyExpandedState(paginatedRows) as RowConfig<T>[];

  // Aplicar estado de edición a las filas
  const rowsWithEditState = useMemo(() => {
    const withEditState = applyEditState(expandedRows);
    return withEditState.map((row) => ({
      ...row,
      _toggleEdit: () => toggleRowEdit(row.id),
    }));
  }, [expandedRows, applyEditState, toggleRowEdit]);

  const {
    selectedIds,
    selectedCount,
    allSelected,
    someSelected,
    handleRowSelect,
    handleSelectAll,
    clearSelection,
  } = useTableSelection(rowsWithEditState, selection?.multiple, {
    isRowSelectable: isRowSelectableForSelection,
    onSelectionChange: selection?.onSelectionChange,
    controlledSelectedIds: selection?.selectedIds,
    accumulativeSelection: selection?.accumulativeSelection,
  });

  // Clave que cambia cuando cambian las filas mostradas (p. ej. al cambiar de página o al cargar nuevos datos).
  // Fuerza que HeaderCell vuelva a calcular los anchos auto cuando hay paginación o los datos cambian.
  const rowsDataKey = useMemo(() => {
    if (paginationConfig?.enabled) {
      const firstId = rowsWithEditState[0]?.id ?? '';
      return `${paginationConfig.currentPage ?? 1}-${rowsWithEditState.length}-${firstId}`;
    }
    const first = rowsWithEditState[0]?.id ?? '';
    const last = rowsWithEditState[rowsWithEditState.length - 1]?.id ?? '';
    return `${rowsWithEditState.length}-${first}-${last}`;
  }, [paginationConfig?.enabled, paginationConfig?.currentPage, rowsWithEditState]);

  const effectiveEmptyState = useMemo(
    () =>
      resolveEffectiveEmptyState({
        empty,
        rowsWithEditStateLength: rowsWithEditState.length,
        internalSearchQuery,
        filtersLength: filters.length,
        initialRowsLength: initialRows.length,
        searchedRowsLength: searchedRows.length,
        filteredRowsLength: filteredRows.length,
      }),
    [
      empty,
      rowsWithEditState.length,
      internalSearchQuery,
      filters.length,
      initialRows.length,
      searchedRows.length,
      filteredRows.length,
    ]
  );

  const handleSortClick = (columnId: string) => {
    const nextSort = getNextSortConfig(sortConfig, columnId);
    handleSort(columnId);
    onSort?.(nextSort);
    // Actualizar configuración persistida con el valor que aplica tras el clic
    if (persistConfig && configKey && nextSort) {
      tableConfig.updateSort(nextSort);
    }
  };

  const handleFilterClick = (columnId: string) => {
    // Aquí se podría abrir un modal o panel de filtros
    // TODO: Implementar lógica de filtros
  };

  const handleLockClick = (columnId: string) => {
    const column = columns.find((col) => col.id === columnId);
    if (!column || !isColumnLockable(column, columnsLockable)) {
      return;
    }
    const nextLocked = !column.locked;
    handleToggleLock(columnId);
    if (persistConfig && configKey) {
      tableConfig.updateColumnLock(columnId, nextLocked);
    }
  };

  const handleResizeColumn = (columnId: string, width: number, defaultWidthAtStart?: number) => {
    const column = columns.find((col) => col.id === columnId);
    if (!column || isActionsColumn(column)) {
      return;
    }
    handleResize(columnId, width);
    onColumnResize?.(columnId, width);
    // Actualizar configuración persistida (defaultWidthAtStart permite volver al ancho inicial al reducir)
    if (persistConfig && configKey) {
      tableConfig.updateColumnWidth(columnId, width, defaultWidthAtStart);
    }
  };

  const handleResizeDoubleClick = (columnId: string) => {
    // Auto-fit: calcular el ancho mínimo necesario basado en el contenido
    // Por ahora, se establece un ancho automático (esto se puede mejorar calculando el contenido real)
    const column = columns.find((col) => col.id === columnId);
    if (!column || isActionsColumn(column)) {
      return;
    }

    // Encontrar el elemento del DOM de la columna para calcular el ancho del contenido
    if (tableRef.current) {
      const headerCell = tableRef.current.querySelector(`th[data-column-id="${columnId}"]`);
      const cells = tableRef.current.querySelectorAll(`td[data-column-id="${columnId}"]`);

      if (headerCell || cells.length > 0) {
        // Calcular el ancho máximo entre el header y todas las celdas
        let maxWidth = 0;

        if (headerCell) {
          const headerContent = headerCell.querySelector('.ui-truncate');
          if (headerContent) {
            maxWidth = Math.max(maxWidth, headerContent.scrollWidth + 80); // +80 para padding y botones
          }
        }

        cells.forEach((cell) => {
          maxWidth = Math.max(maxWidth, cell.scrollWidth + 32); // +32 para padding
        });

        // Establecer un mínimo y máximo razonable
        const autoWidth = Math.min(Math.max(maxWidth, 100), 500);
        handleResize(columnId, autoWidth);
        onColumnResize?.(columnId, autoWidth);
      }
    }
  };

  const handleResizeHover = (columnId: string, isHovering: boolean) => {
    setResizeHoverColumnId(isHovering ? columnId : null);
  };

  const handleSearch = (query: string) => {
    setInternalSearchQuery(query);
    toolbarConfig?.onSearch?.(query);
    if (persistConfig && configKey && queryPersist) {
      tableConfig.setSearchQuery(query);
    }
  };

  const handleColumnVisibilityChange = (columnId: string, visible: boolean) => {
    handleSetVisibility(columnId, visible);
    onColumnVisibilityChange?.(columnId, visible);
    // Actualizar configuración persistida
    if (persistConfig && configKey) {
      tableConfig.updateColumnVisibility(columnId, visible);
    }
  };

  const handleMultipleColumnVisibilityChange = (
    updates: Array<{ columnId: string; visible: boolean }>
  ) => {
    // Aplicar todos los cambios de visibilidad de una vez
    updates.forEach(({ columnId, visible }) => {
      handleSetVisibility(columnId, visible);
      onColumnVisibilityChange?.(columnId, visible);
    });
    // Actualizar configuración persistida en batch para evitar sobreescrituras
    if (persistConfig && configKey) {
      tableConfig.updateMultipleColumnVisibility(updates);
    }
  };

  const handleColumnReorder = (reorderedColumns: ColumnConfig<unknown>[]) => {
    // Aplicar el nuevo orden completo de una vez
    const newOrder = reorderedColumns.map((col) => col.id);
    setColumnsOrder(newOrder);

    onColumnReorder?.(reorderedColumns as ColumnConfig<T>[]);
    // Actualizar configuración persistida
    if (persistConfig && configKey) {
      tableConfig.updateColumnOrder(reorderedColumns);
    }
  };

  const handleRowExpandClick = (row: RowConfig<T>) => {
    handleExpand(row);
    onRowExpand?.(row);
  };

  const handleCellChange = (rowId: string | number, columnId: string, value: unknown) => {
    // Marcar esta fila como editada
    editedRowIdsRef.current.add(rowId);

    // Encontrar los datos de la fila
    const row = initialRows.find((r) => r.id === rowId);

    if (onHandleChange) {
      onHandleChange(rowId, columnId, value, row);
    }
  };

  // Detectar cuando se cierran filas editadas y ejecutar onSuccessComplete
  useEffect(() => {
    if (!onSuccessComplete) return;

    const currentEditingIds = new Set(editingRowIds || []);
    const prevEditingIds = prevEditingRowIdsRef.current;

    // Filas que se cerraron (estaban antes pero no ahora)
    const closedRowIds: (string | number)[] = [];
    prevEditingIds.forEach((rowId) => {
      if (!currentEditingIds.has(rowId)) {
        closedRowIds.push(rowId);
      }
    });

    // Si hay filas cerradas que fueron editadas, ejecutar el callback UNA SOLA VEZ
    if (closedRowIds.length > 0) {
      const editedClosedRows = closedRowIds.filter((rowId) => editedRowIdsRef.current.has(rowId));

      if (editedClosedRows.length > 0) {
        // Ejecutar el callback UNA SOLA VEZ con todas las filas editadas
        onSuccessComplete(editedClosedRows);
        // Limpiar el tracking de las filas que se cerraron
        editedClosedRows.forEach((rowId) => {
          editedRowIdsRef.current.delete(rowId);
        });
      }
    }

    // Actualizar la referencia para la próxima vez
    prevEditingRowIdsRef.current = new Set(currentEditingIds);
  }, [editingRowIds, onSuccessComplete]);

  const handlePageChange = (page: number) => {
    paginationConfig?.onPageChange?.(page);
  };

  const handlePageSizeChange = (size: number) => {
    paginationConfig?.onPageSizeChange?.(size);
  };

  const containerClasses = cn(
    'ui-imj ui-relative ui-flex ui-h-full ui-w-full ui-max-w-full ui-flex-col ui-overflow-hidden ui-bg-fill ui-rounded-surface-sheet ui-p-16',
    borderable && 'ui-border ui-border-default',
    className
  );

  const handleFiltersApply = (newFilters: AdvancedFilterConfig[]) => {
    setAllFilters(newFilters);

    // Si internalFilters es false, emitir el evento onFilter con los filtros aplicados
    const shouldApplyInternalFilters = toolbarConfig?.internalFilters ?? false;
    if (!shouldApplyInternalFilters && onFilter) {
      onFilter(newFilters);
    }
  };

  const selectionBarPlacement = selection?.selectionBarPlacement ?? 'inline';
  const showSelectionBarUi = Boolean(selection?.showSelectionBar && selectedCount > 0);

  return (
    <div ref={tableRef} className={containerClasses}>
      {toolbarConfig && (
        <TableToolbar
          {...toolbarConfig}
          searchValue={internalSearchQuery}
          showFilters={toolbarConfig.showFilters ?? true}
          selectedCount={selectedCount}
          selectedIds={selectedIds}
          columns={columns as ColumnConfig<unknown>[]}
          rows={initialRows as RowConfig<unknown>[]}
          filters={filters}
          onSearch={handleSearch}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          onMultipleColumnVisibilityChange={handleMultipleColumnVisibilityChange}
          onColumnReorder={handleColumnReorder}
          onFiltersApply={handleFiltersApply}
        />
      )}

      <div className="ui-min-h-0 ui-min-w-0 ui-flex-1 ui-overflow-hidden">
        <TableContent<T>
          columns={columns}
          rows={rowsWithEditState}
          loading={loading}
          empty={effectiveEmptyState}
          selection={selection}
          isRowSelectionDisabled={
            excludedRows ? (row) => isRowExcludedFromSelection(row, excludedRows) : undefined
          }
          sort={sortConfig}
          filters={filters}
          expandable={expandable}
          resizable={resizable}
          columnsLockable={columnsLockable}
          resizeHoverColumnId={resizeHoverColumnId}
          striped={striped}
          bordered={bordered}
          hoverable={hoverable}
          dense={dense}
          stickyHeader={stickyHeader}
          stickyCheckbox={stickyCheckbox}
          stickyActions={stickyActions}
          selectedIds={selectedIds}
          allSelected={allSelected}
          someSelected={someSelected}
          onSelectAll={handleSelectAll}
          onRowSelect={handleRowSelect}
          onSort={handleSortClick}
          onFilter={handleFilterClick}
          onLock={handleLockClick}
          onResize={handleResizeColumn}
          onResizeDoubleClick={handleResizeDoubleClick}
          onResizeHover={handleResizeHover}
          onRowClick={onRowClick}
          onRowExpand={handleRowExpandClick}
          onCellFocus={handleCellFocus}
          isCellFocused={isCellFocused}
          onCellChange={handleCellChange}
          rowsDataKey={rowsDataKey}
        />
      </div>

      {/* Barra de selección: overlay (flotante) o inline (debajo del área de filas) */}
      {showSelectionBarUi &&
        selection &&
        (selectionBarPlacement === 'inline' ? (
          <div className="ui-flex ui-shrink-0 ui-justify-center ui-border-t ui-border-default ui-px-8 ui-pb-4 ui-pt-12">
            <TableSelectionBar
              selectedCount={selectedCount}
              selectedIds={selectedIds}
              actions={selection.actions}
              selectionText={selection.selectionText}
              clearSelectionTooltip={selection.clearSelectionTooltip}
              onClearSelection={selection.showClearSelection ? clearSelection : undefined}
            />
          </div>
        ) : (
          <div
            className={cn(
              'ui-pointer-events-none ui-absolute ui-left-0 ui-right-0 ui-z-50 ui-flex ui-justify-center',
              paginationConfig?.enabled ? 'ui-bottom-20' : 'ui-bottom-8'
            )}
          >
            <TableSelectionBar
              selectedCount={selectedCount}
              selectedIds={selectedIds}
              actions={selection.actions}
              selectionText={selection.selectionText}
              clearSelectionTooltip={selection.clearSelectionTooltip}
              onClearSelection={selection.showClearSelection ? clearSelection : undefined}
              className="ui-pointer-events-auto"
            />
          </div>
        ))}

      {paginationConfig?.enabled && rowsWithEditState.length > 0 && (
        <Pagination
          currentPage={paginationConfig.currentPage || 1}
          pageCount={Math.ceil(
            (paginationConfig.total !== undefined ? paginationConfig.total : totalAfterFilters) /
              (paginationConfig.pageSize || 10)
          )}
          pageSize={paginationConfig.pageSize || 10}
          total={paginationConfig.total !== undefined ? paginationConfig.total : totalAfterFilters}
          onPageChange={handlePageChange}
          handlePageSize={handlePageSizeChange}
          showVisiblePages
          showPagesSelector
        />
      )}
    </div>
  );
};
